import { Injectable } from '@angular/core';

/**
 * A service for persisting filter configurations in localStorage by list identifier.
 * Supports versioning, partial updates, and full resets.
 */
@Injectable({ providedIn: 'root' })
export class FilterPrefsService {
    /**
     * Bump this version when changing storage schema to avoid key collisions.
     */
    private readonly VERSION = 'v1';

    /**
     * Prefix for all filter storage keys. Full key: `${PREFIX}:${VERSION}:${listId}`
     */
    private readonly PREFIX = 'dataListFilters';

    /**
     * Formats the storage key for a given listId.
     * @param listId Unique identifier for the list (e.g., 'ordersList').
     */
    private storageKeyFor(listId: string): string {
        return `${this.PREFIX}:${this.VERSION}:${listId}`;
    }

    /**
     * Retrieves saved filter values for the given listId.
     * Returns an object mapping field names to stored values, or null if none.
     */
    getFilters(listId: string): Record<string, any> | null {
        try {
            const key = this.storageKeyFor(listId);
            const raw = localStorage.getItem(key);
            if (!raw) {
                return null;
            }
            const parsed = JSON.parse(raw);
            if (
                parsed &&
                typeof parsed === 'object' &&
                !Array.isArray(parsed)
            ) {
                return parsed;
            }
            return null;
        } catch {
            // On parse error or other issues, ignore and return null
            return null;
        }
    }

    /**
     * Saves the full set of filter values for a given listId, overwriting any previous data.
     * @param listId Unique identifier for the list.
     * @param values A record of filter field names to their values.
     */
    setFilters(listId: string, values: Record<string, any>): void {
        try {
            const key = this.storageKeyFor(listId);
            localStorage.setItem(key, JSON.stringify(values));
        } catch {
            console.warn(
                `FilterPrefsService: unable to write filters to localStorage for listId="${listId}"`
            );
        }
    }

    /**
     * Partially updates stored filters for listId, merging new values into existing ones.
     * If no existing data, behaves like setFilters().
     * @param listId Unique identifier for the list.
     * @param partial A record of fields to update. Fields set to null will be removed.
     */
    updateFilters(listId: string, partial: Record<string, any>): void {
        const existing = this.getFilters(listId) || {};
        // Remove any keys explicitly set to null
        Object.keys(partial).forEach((key) => {
            if (partial[key] === null) {
                delete existing[key];
            } else {
                existing[key] = partial[key];
            }
        });
        this.setFilters(listId, existing);
    }

    /**
     * Clears all saved filters for the specified list, without affecting other lists.
     */
    clear(listId: string): void {
        const key = this.storageKeyFor(listId);
        localStorage.removeItem(key);
    }

    /**
     * Removes saved filters for all listIds under this version.
     * Use with caution: this will wipe every instance.
     */
    clearAllInstances(): void {
        const prefix = `${this.PREFIX}:${this.VERSION}:`;
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const fullKey = localStorage.key(i);
            if (fullKey && fullKey.startsWith(prefix)) {
                localStorage.removeItem(fullKey);
            }
        }
    }
}
